import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { onConnectionError } from "services/generic/AxiosInterceptor/onResponseRejected"

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const retry = (failureCount: number, error: any) =>
  // Fail a maximum of 3 times total (including the last extra attempt)
  failureCount < 2 &&
  // Don't retry on errors that are guaranteed to fail again
  !(isAxiosError(error) && [400, 401, 403, 404, 405, 413, 414, 429].includes(error.response?.status ?? 0))

// Retry with exponential backoff 1s, 3s, 9s
const retryDelay = (failureCount: number) => 1000 * 3 ** failureCount

export const queryClient = new QueryClient({
  // `onError` runs once all retries failed, so a request without a response is toasted and reported once
  // instead of per attempt. A query a component still shows earlier data for failed in the background (a
  // poll or refetch): the user can keep working with that data, so it is left alone. A query without
  // observers was fetched by an awaited `fetchQuery`, whose caller gets the failure instead of cached data.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data === undefined || query.getObserversCount() === 0) {
        onConnectionError(error)
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => onConnectionError(error),
  }),
  defaultOptions: {
    queries: {
      retry,
      retryDelay,
    },
    mutations: {
      // No automatic retry: a mutation that timed out or failed on the way back may well have been applied
      // already, and a repeat sent by itself applies it a second time (e.g. a duplicate import or email).
      retry: false,
    },
  },
})

declare global {
  interface Window {
    queryClient: QueryClient
  }
}

// Expose the QueryClient to e2e tests for tests involving cache invalidations
if (!import.meta.env.PROD) {
  window.queryClient = queryClient
}
